import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Icon, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Tooltip } from '@material-ui/core';
import { green, red, orange } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';
import webrtcPhone from 'app/services/webrtcPhone';

var useStyles = makeStyles(function(theme) { return {
    root: { position: 'fixed', bottom: 20, right: 20, zIndex: 9999 },
    phoneButton: { width: 56, height: 56, backgroundColor: green[500], color: '#fff', '&:hover': { backgroundColor: green[700] } },
    phoneButtonOff: { width: 56, height: 56, backgroundColor: theme.palette.grey[400], color: '#fff', '&:hover': { backgroundColor: theme.palette.grey[600] } },
    inCallButton: { width: 56, height: 56, backgroundColor: red[500], color: '#fff', '&:hover': { backgroundColor: red[700] } },
    ringingButton: { width: 56, height: 56, backgroundColor: orange[500], color: '#fff', animation: '$pulse 1s infinite', '&:hover': { backgroundColor: orange[700] } },
    '@keyframes pulse': { '0%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.1)' }, '100%': { transform: 'scale(1)' } },
    statusText: { position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 10, whiteSpace: 'nowrap', color: theme.palette.text.secondary },
    callInfo: { padding: theme.spacing(2), textAlign: 'center' }
}; });

function WebRTCPhone() {
    var _s = useState(false), registered = _s[0], setRegistered = _s[1];
    var _c = useState('idle'), callState = _c[0], setCallState = _c[1];
    var _ci = useState(null), callerInfo = _ci[0], setCallerInfo = _ci[1];
    var _d = useState(0), callDuration = _d[0], setCallDuration = _d[1];
    var classes = useStyles();

    var userData = useSelector(function(state) { return state.auth && state.auth.user && state.auth.user.data; });
    var sipPhone = (userData && userData.sipPhone) || '101';

    useEffect(function() {
        var ext = 'webrtc-' + sipPhone;
        var pass = 'webrtc' + sipPhone;
        webrtcPhone.register(ext, pass);

        var onReg = function() { setRegistered(true); };
        var onUnreg = function() { setRegistered(false); };
        var onIncoming = function(data) {
            if (!data.isFromCMS) {
                setCallState('incoming');
                setCallerInfo({ from: data.from, displayName: data.displayName });
            }
        };
        var onRinging = function() {
            setCallState(function(prev) { return prev === 'incoming' ? 'incoming' : 'ringing'; });
        };
        var onConnected = function() { setCallState('connected'); };
        var onEnded = function() { setCallState('idle'); setCallerInfo(null); setCallDuration(0); };
        var onFailed = function() { setCallState('idle'); setCallerInfo(null); };

        webrtcPhone.on('registered', onReg);
        webrtcPhone.on('unregistered', onUnreg);
        webrtcPhone.on('incomingCall', onIncoming);
        webrtcPhone.on('ringing', onRinging);
        webrtcPhone.on('callConnected', onConnected);
        webrtcPhone.on('callEnded', onEnded);
        webrtcPhone.on('callFailed', onFailed);

        return function() {
            webrtcPhone.off('registered', onReg);
            webrtcPhone.off('unregistered', onUnreg);
            webrtcPhone.off('incomingCall', onIncoming);
            webrtcPhone.off('ringing', onRinging);
            webrtcPhone.off('callConnected', onConnected);
            webrtcPhone.off('callEnded', onEnded);
            webrtcPhone.off('callFailed', onFailed);
        };
    }, [sipPhone]);

    useEffect(function() { var i; if (callState === 'connected') { i = setInterval(function() { setCallDuration(function(d) { return d + 1; }); }, 1000); } return function() { clearInterval(i); }; }, [callState]);

    var handleAnswer = useCallback(function() {
        webrtcPhone.answer();
        setCallState('connected');
    }, []);

    var handleReject = useCallback(function() {
        if (webrtcPhone.currentSession) {
            webrtcPhone.currentSession.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
            webrtcPhone.currentSession = null;
        }
        setCallState('idle');
        setCallerInfo(null);
    }, []);

    var handleHangup = useCallback(function() {
        webrtcPhone.hangup();
        setCallState('idle');
        setCallerInfo(null);
        setCallDuration(0);
    }, []);

    var formatDuration = function(s) { var m = Math.floor(s/60).toString().padStart(2,'0'); var sec = (s%60).toString().padStart(2,'0'); return m + ':' + sec; };

    var getStatusText = function() {
        if (!registered) return 'Offline';
        switch(callState) {
            case 'ringing': return '\u0110ang \u0111\u1ed5 chu\u00f4ng...';
            case 'incoming': return 'Cu\u1ed9c g\u1ecdi \u0111\u1ebfn!';
            case 'connected': return formatDuration(callDuration);
            default: return 'Online (' + sipPhone + ')';
        }
    };

    var getButtonClass = function() {
        if (callState === 'connected') return classes.inCallButton;
        if (callState === 'incoming') return classes.ringingButton;
        if (callState === 'ringing') return classes.inCallButton;
        return registered ? classes.phoneButton : classes.phoneButtonOff;
    };

    return React.createElement('div', { className: classes.root },
        React.createElement(Tooltip, { title: getStatusText(), placement: 'left' },
            React.createElement(IconButton, {
                className: getButtonClass(),
                onClick: (callState === 'connected' || callState === 'ringing') ? handleHangup : undefined
            },
                callState === 'connected' ? React.createElement(Icon, null, 'call_end') :
                callState === 'incoming' ? React.createElement(Badge, { badgeContent: '!', color: 'error' }, React.createElement(Icon, null, 'phone_callback')) :
                callState === 'ringing' ? React.createElement(Icon, null, 'call_end') :
                React.createElement(Icon, null, 'phone')
            )
        ),
        React.createElement('div', { className: classes.statusText }, getStatusText()),
        React.createElement(Dialog, { open: callState === 'incoming', maxWidth: 'xs', fullWidth: true },
            React.createElement(DialogTitle, { style: { backgroundColor: green[600], color: '#fff', textAlign: 'center' } }, 'Cu\u1ed9c g\u1ecdi \u0111\u1ebfn'),
            React.createElement(DialogContent, { className: classes.callInfo },
                React.createElement(Icon, { style: { fontSize: 64, color: green[500], marginTop: 16 } }, 'phone_in_talk'),
                React.createElement(Typography, { variant: 'h4', style: { marginTop: 16, fontWeight: 'bold' } }, (callerInfo && callerInfo.from) || 'Kh\u00f4ng x\u00e1c \u0111\u1ecbnh'),
                React.createElement(Typography, { variant: 'body1', color: 'textSecondary', style: { marginTop: 8 } }, (callerInfo && callerInfo.displayName) || 'S\u1ed1 \u0111i\u1ec7n tho\u1ea1i kh\u00e1ch h\u00e0ng')
            ),
            React.createElement(DialogActions, { style: { justifyContent: 'center', paddingBottom: 24, paddingTop: 16 } },
                React.createElement(Button, {
                    variant: 'contained',
                    size: 'large',
                    style: { backgroundColor: red[500], color: '#fff', marginRight: 24, minWidth: 140, borderRadius: 28 },
                    onClick: handleReject,
                    startIcon: React.createElement(Icon, null, 'call_end')
                }, 'T\u1eeb ch\u1ed1i'),
                React.createElement(Button, {
                    variant: 'contained',
                    size: 'large',
                    style: { backgroundColor: green[500], color: '#fff', minWidth: 140, borderRadius: 28 },
                    onClick: handleAnswer,
                    startIcon: React.createElement(Icon, null, 'call')
                }, 'Nghe m\u00e1y')
            )
        )
    );
}

export default WebRTCPhone;
